import React, { useState, useEffect, useRef, useCallback } from 'react';

import { mount, unmount, setChildren } from 'redom';

import { Node } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { HistoryEditor } from 'slate-history';

import HelptapEditor from '../../helpers';

import BaseElement from '../base';

import NativeVideoCanvas from '../canvas/canvas';
import {
  waitForResources
} from '../canvas/utilities';
import VideoBuilder from './builder';

import { ReactComponent as PositionControlIcon } from 'assets/images/position-control-icon.svg';

import './index.scss';

function VideoCanvas(props) {

  const element = props.element;

  const editor = useSlateStatic();

  const elementPath = ReactEditor.findPath(editor, props.element);
  const unloadedReference = useRef(false);
  const nativeVideoCanvasReference = useRef(null);
  const videoBuilderReference = useRef(null);
  const containerReference = useRef(null);

  const [rootState, setRootState] = useState({
    loadedPercent: 0
  });

  const updateProperties = useCallback(function updateProperties(properties) {

    const updatedNode = Node.get(editor, elementPath);

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...updatedNode.properties,
        ...properties
      }
    }, elementPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onLoad() {

    return initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onPropertiesChange() {

    if (nativeVideoCanvasReference.current !== null) {
      nativeVideoCanvasReference.current.update(structuredClone(element), props.mediaUploader);
    }
    if (videoBuilderReference.current !== null) {
      videoBuilderReference.current.update(structuredClone(element.properties));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.width, element.height, element.backgroundColor, element.properties]);

  useEffect(() => {
    // eslint-disable-next-line func-style
    const handleKeydown = (event) => {
      const isUndo = (event.metaKey || event.ctrlKey) && event.key === 'z';
      const isRedo = (event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'Z';

      if (isUndo || isRedo) {
        event.preventDefault();
        if (isRedo) {
          HistoryEditor.redo(editor);
        } else if (isUndo) {
          HistoryEditor.undo(editor);
        }
      }
    };

    // Attach the event listener to the Fabric.js canvas container
    const canvasEl = containerReference.current;
    canvasEl.addEventListener('keydown', handleKeydown);

    // Ensure Fabric.js canvas is focusable
    canvasEl.tabIndex = 0;

    return () => {
      canvasEl.removeEventListener('keydown', handleKeydown);
    };
  }, [editor]);

  function setLoadedPercent(loadedPercent) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        loadedPercent
      };

    });

  }

  function initialize() {

    const container = containerReference.current;

    const loader = waitForResources(element.properties.data);

    // eslint-disable-next-line no-console
    loader.on('progress', (progress) => { setLoadedPercent(progress); });
    loader.on('done', () => {

      if (unloadedReference.current === true) {
        return;
      }

      const canvas = new NativeVideoCanvas(element, props.mediaUploader, updateProperties, 'video', () => {

        if (unloadedReference.current === true) {
          return;
        }

        videoBuilderReference.current = new VideoBuilder(element, canvas, updateProperties, container);
        container.videoBuilder = videoBuilderReference.current;

        mount(container, videoBuilderReference.current);
      });

      nativeVideoCanvasReference.current = canvas;

      container.canvas = nativeVideoCanvasReference.current;

      setChildren(container, [nativeVideoCanvasReference.current]);

    });

    return uninitialize.bind(null, container);

  }

  function uninitialize(container) {

    unloadedReference.current = true;

    if (videoBuilderReference.current !== null) {
      videoBuilderReference.current.stop();
    }

    if (nativeVideoCanvasReference.current !== null) {
      nativeVideoCanvasReference.current.onunmount();
    }

  }

  function toggleVideoBuilder(event) {

    const videoBuilder = videoBuilderReference.current;
    if (videoBuilder === null) {
      return;
    }
    if (videoBuilder.displayed === true) {
      videoBuilder.hide();
    } else {
      videoBuilder.show(event.target, containerReference.current);
    }
  }

  function renderContent() {

    const mainAttributes = {
      ...props.attributes,
      contentEditable: false,
      className: 'canvas-content'
    };

    const containerAttributes = {
      className: 'canvas-container',
      ref: containerReference,
      tabIndex: 0,
      onKeyDown(event) {
        // if command + d or ctrl + d is pressed, duplicate the active object
        if ((event.metaKey || event.ctrlKey) && event.key === 'd') {
          event.preventDefault();
          event.stopPropagation();
          nativeVideoCanvasReference.current.duplicateActiveObject();
        }
      },
      onMouseLeave() {
        const videoBuilder = videoBuilderReference.current;
        if (videoBuilder === null) {
          return;
        }
        videoBuilder.hide();
      },
      onMouseEnter() {
        const videoBuilder = videoBuilderReference.current;
        if (videoBuilder === null) {
          return;
        }
        videoBuilder.show(containerReference.current, containerReference.current);
      }
    };

    const videoBuilderToggleAttributes = {
      type: 'button',
      id: 'toggle-video-builder-button',
      onClick: toggleVideoBuilder
    };

    return <div {...mainAttributes}>
      <div {...containerAttributes}>
        <div className="canvas-loader-display">
          <ht-spinner size="small" />
          <div className="canvas-loader-text sub-text">{rootState.loadedPercent}%</div>
        </div>
      </div>
      <button {...videoBuilderToggleAttributes}>
        <PositionControlIcon />
      </button>
      {props.children}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'video-canvas-main'
    };

    return <BaseElement {...baseElementProperties}>
      {renderContent()}
    </BaseElement>;

  }

  return renderMain();

}

export default VideoCanvas;